/*
 * Collection and search results page enhancements (LDJ).
 *
 * Runs beside the search component in theme.js and never changes it. It reads
 * the markup that component renders and drives filters through the component's
 * own [data-filter-input] change handler, so search, analytics and URLs behave
 * exactly as before.
 *
 *  1. Cards: sized images for search-rendered cards, photo loading state, and
 *     the same retail + condition lines the first-page Liquid cards use, so
 *     nothing shifts when search results replace them.
 *  2. Quick chips: "Available now" plus the top designers (one house: its top
 *     colours and leathers); in the toolbar between the count and the sort on
 *     desktop, under the title on phones.
 *  3. Filter sidebar: sensible order, remembered open sections, readable labels,
 *     color swatches, a search box for long lists, selected counts, an own
 *     min–max price range, and a column sized to the visible screen.
 *  4. Toolbar: current sort shown, "Clear all", sticky shadow.
 *  5. Scroll back to the top of the grid when filters change deep in the page.
 *  6. "Read more" for long collection descriptions.
 *  8. Prices in the shopper's currency: search cards print the shop's own
 *     price (CAD) behind a bare "$"; they are converted with Shopify's rate
 *     and written as Shopify writes that currency, like the Liquid cards.
 *  7. Search page only: the section (sections/search.liquid) is left as it
 *     is, so the heading, the sort label, "Clear all", the result count and
 *     the empty state that the collection section has in its markup are
 *     added here. The search form moves into the heading only after the
 *     component has taken its references to it.
 *  9. Loading more never stalls: when the shopper reaches or passes the end
 *     of the grid, the next page is asked for as soon as the component is free.
 * 10. Phones: where the component stops (a few hundred pieces), the end of the
 *     grid shows the count, "Show more" and "Filter".
 *
 * Smoothness rules this file keeps (Sept 2026 filter-panel performance pass):
 *  - Nothing reads layout or computed style on every scroll frame: the sticky
 *    toolbar's shadow comes from an IntersectionObserver, and the desktop
 *    column's height is only re-measured while scrolling on desktop.
 *  - Every DOM write is skipped when it would not change anything. Shopify's
 *    pixel manager observes the whole document (attributes and subtrees) and
 *    serializes each change with a layout read per node, so a redundant write
 *    costs far more than the write itself.
 *  - Filter groups move as little as possible when they are put in order, and
 *    colour swatches are only painted into groups that are open.
 */
(() => {
  'use strict';

  const shell = document.querySelector('.page-shell--search');
  const search = shell && shell.querySelector('luxe-search');
  if (!search || shell.dataset.plpReady !== undefined) return;
  shell.dataset.plpReady = '';
  if (!shell.classList.contains('page-shell--collection-products')) setUpSearchPage();

  const results = search.querySelector('[data-search-results]');
  const facets = search.querySelector('[data-facets]');
  const active = search.querySelector('[data-active-filters]');
  const toolbar = search.querySelector('[data-search-toolbar]');
  const count = search.querySelector('[data-search-count]');
  const sortSelect = search.querySelector('[data-search-sort]');
  const sortValue = search.querySelector('[data-sort-value]');
  const clearAll = search.querySelector('[data-plp-clear-all]');
  const layout = search.querySelector('.luxe-search__layout');
  const quick = shell.querySelector('[data-plp-quick]');
  const mobileQuery = window.matchMedia('(max-width: 989px)');
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  // Fallback only (pages without the layout's money sample): the shop's own currency, CAD.
  const money = new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 });

  const toNumber = (text) => {
    const number = Number(String(text || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(number) ? number : 0;
  };
  const fold = (text) => String(text || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const soldArchive = search.dataset.collection === 'sold-collection';

  /* 8. Prices in the shopper's currency ---------------------------------------- */

  // The search service stores the shop's own price (CAD), and the component
  // prints it behind a bare "$" for everyone, while the first page (Liquid),
  // the home page and the product page show the shopper's currency. Convert
  // with Shopify's own rate and write the amount the way Shopify writes that
  // currency: the layout prints a sample amount in it (#LdjMoney).
  const shopCurrency = 'CAD';
  const shopperCurrency = window.Shopify?.currency?.active || shopCurrency;
  const shopperMoney = (() => {
    let sample = '';
    try {
      sample = JSON.parse(document.getElementById('LdjMoney')?.textContent || '{}').sample || '';
    } catch (_error) {
      sample = '';
    }
    const decoder = document.createElement('textarea');
    decoder.innerHTML = sample.replace(/<[^>]*>/g, '');
    sample = decoder.value;
    const first = sample.search(/\d/);
    if (first < 0) return null;
    const last = sample.length - Array.from(sample).reverse().join('').search(/\d/);
    const middle = sample.slice(first, last);
    const digits = middle.split(/\D+/);
    const separators = middle.match(/\D+/g) || [];
    // "1,234,567.89", "1.234.567,89", "1 234 567,89", "1,234,568" (no cents)
    const cents = digits.length > 1 && digits[digits.length - 1].length === 2;
    const decimal = cents ? separators[separators.length - 1] : '';
    const group = cents ? (separators.length > 1 ? separators[0] : '') : separators[0] || '';
    const prefix = sample.slice(0, first);
    const suffix = sample.slice(last);
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    // An amount already in the shopper's currency, written the way Shopify writes it.
    const format = (amount) => {
      const total = Math.round(amount * 100);
      const whole = cents ? Math.floor(total / 100) : Math.round(total / 100);
      const fraction = cents ? total % 100 : 0;
      const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, group);
      // Like money_without_trailing_zeros: no ".00".
      return `${prefix}${fraction ? `${grouped}${decimal}${String(fraction).padStart(2, '0')}` : grouped}${suffix}`;
    };
    // A shop-currency price converted, rounded the way this market rounds (see LdjMoneyRule).
    /* TAP-013 | Original conversion logic — retained for temporary rollback reference
    const convert = (base) => {
      const value = Number(base);
      if (!Number.isFinite(value) || value <= 0) return '';
      const converted = value * rate;
      return format(window.LdjMoneyRule?.get() === 'whole' ? Math.round(converted) : converted);
    };
    */

    // TAP-013 | US Collection Price Fix — 2026-10-29
    const convert = (base) => {
      const value = Number(base);
      if (!Number.isFinite(value) || value <= 0) return '';

      const converted = value * rate;
      const rule = window.LdjMoneyRule?.get();

      let amount = converted;

      if (rule === 'whole') {
        amount = shopperCurrency === 'USD'
          ? Math.ceil(converted - 1e-9)
          : Math.round(converted);
      }

      return format(amount);
    };
    
    convert.format = format;
    return convert;
  })();

  /* Sale pieces on cards drawn by the search component: the old price struck through,
     "N% Off" and a Sale tag on the photo, as snippets/price.liquid and product-card.liquid
     print them on the first page. The compare-at prices come with the search service's
     answers (kept by the piece's link in window.LdjSaleInfo by the layout's fetch wrapper, so
     two units of one model, each its own card in a search, keep their own prices). */
  const saleMoney = (amount) => (shopperMoney ? shopperMoney(amount) : money.format(amount));
  const decorateSale = (card) => {
    if (!card.classList.contains('luxe-product-card')) return;
    const link = card.querySelector('[data-product-url]')?.dataset.productUrl;
    const info = window.LdjSaleInfo?.get(link && window.LdjSaleKey ? window.LdjSaleKey(link) : card.dataset.productHandle);
    const items = card.querySelector('.product-card__price .fp-price-items');
    const base = Number(card.querySelector('[data-product-price]')?.dataset.productPrice);
    if (!info || !items || !(base > 0) || !(info.compare > base)) return;
    const compareText = saleMoney(info.compare);
    const off = Math.round(((info.compare - base) * 100) / info.compare);
    if (!compareText) return;
    items.classList.add('fp-price-items--sale');
    let compare = items.querySelector('.fp-price-compare');
    if (!compare) {
      const label = document.createElement('span');
      label.className = 'sr-only';
      label.textContent = 'Original price';
      compare = document.createElement('s');
      compare.className = 'fp-price-compare';
      items.append(label, compare);
    }
    if (compare.textContent !== compareText) compare.textContent = compareText;
    let tag = items.querySelector('.fp-price-off');
    if (off >= 1) {
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'fp-price-off';
        items.append(tag);
      }
      if (tag.textContent !== `${off}% Off`) tag.textContent = `${off}% Off`;
    }
    const badges = card.querySelector('.product-card__badges');
    // Sold says so instead: a Sale tag only on pieces that can be bought (a pre-order piece can).
    if (badges && !badges.querySelector('.badge:not(.badge--sale):not(.badge--preorder)') && !badges.querySelector('.badge--sale')) {
      const badge = document.createElement('span');
      badge.className = 'badge badge--sale';
      badge.textContent = 'Sale';
      badges.prepend(badge);
    }
  };
  document.addEventListener('ldj:sale-info', () => {
    search.querySelectorAll('.luxe-product-card').forEach((card) => decorateSale(card));
  });

  const priceInShopperCurrency = (card) => {
    if (!shopperMoney || !card.classList.contains('luxe-product-card')) return;
    const base = card.querySelector('[data-product-price]')?.dataset.productPrice;
    const price = card.querySelector('.product-card__price');
    // The first converted card asks Shopify how this market rounds; every card is re-priced once known.
    if (shopperCurrency !== shopCurrency && !window.LdjMoneyRule?.get()) {
      window.LdjMoneyRule?.probe([{ handle: card.dataset.productHandle, base: Number(base) }], () => {
        search.querySelectorAll('.luxe-product-card').forEach((item) => priceInShopperCurrency(item));
      });
    }
    const text = shopperMoney(base);
    if (!price || !text) return;
    const current = price.childElementCount === 1 && price.firstElementChild.classList.contains('fp-price-items')
      ? price.firstElementChild.querySelector('.fp-price-item')
      : null;
    if (!current || current.textContent.trim() !== text) {
      const items = document.createElement('span');
      items.className = 'fp-price-items';
      const item = document.createElement('span');
      item.className = 'fp-price-item';
      item.textContent = text;
      items.append(item);
      price.replaceChildren(items);
    }
    decorateSale(card);
  };

  // The price buckets are round numbers in the shopper's currency (the service converts
  // them with Shopify's rate): write the labels in that currency's own format.
  const PRICE_RANGE_VALUES = new Set(['Under1000', 'From1000To2000', 'From2000To5000', 'From5000To8000', 'Over8000']);
  // Same scale as the service (priceBucketScale): rupees and yen get the limits × 100.
  const priceScale = () => {
    if (shopperCurrency === shopCurrency) return 1;
    const rate = Number(window.Shopify?.currency?.rate) || 1;
    return rate === 1 ? 1 : 10 ** Math.round(Math.log10(rate));
  };
  const shopperPriceText = (text) => (shopperCurrency === shopCurrency || !shopperMoney
    ? text
    : text.replace(/\$([\d,]+(?:\.\d+)?)/g, (_match, digits) => shopperMoney.format(Number(digits.replace(/,/g, '')) * priceScale())));
  const labelPriceRange = (element) => {
    if (shopperCurrency === shopCurrency || !shopperMoney || !element || element.dataset.plpCurrency) return;
    const text = element.textContent.trim();
    if (!text.includes('$')) return;
    element.dataset.plpCurrency = '';
    element.textContent = shopperPriceText(text);
  };
  const escapeSelector = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));

  /* Theme animations. theme.js used to stagger product cards and filter groups in with
     GSAP, which rewrites every element's inline style on every frame for up to a second
     (and reads computed style as each one starts); Shopify's pixel manager then re-reads
     the layout of each element it rewrote. Since Sept 2026 theme.js uses Web Animations
     (no inline styles) and this finds nothing to stop. It stays as a guard: should a GSAP
     tween start on cards or filter groups again, it is stopped right away, and a fresh
     page of results fades in as one layer instead (collection.css).
     Set to false to leave any such tween alone. */
  const SETTLE_THEME_TWEENS = true;
  const TWEENED = ['opacity', 'visibility', 'transform', 'translate', 'scale', 'rotate'];
  const clearTweened = (elements) => {
    elements.forEach((element) => {
      if (!element.hasAttribute('style')) return;
      TWEENED.forEach((property) => {
        if (element.style.getPropertyValue(property)) element.style.removeProperty(property);
      });
      if (element.getAttribute('style') === '') element.removeAttribute('style');
    });
  };
  const settleTweens = (targets) => {
    const gsap = window.gsap;
    if (!SETTLE_THEME_TWEENS || !gsap || typeof gsap.getTweensOf !== 'function' || !targets.length) return 0;
    // All of them, staggered starts still pending included (GSAP's isTweening sees started ones).
    const tweens = gsap.getTweensOf(targets);
    if (!tweens.length) return 0;
    // kill() with no arguments stops the whole tween without rendering anything more.
    // Finishing or reverting it instead makes GSAP start every staggered part at once, each
    // reading computed style. The inline styles it already wrote are taken off now, and again
    // after GSAP's next two ticks, which can still write a deferred ("lazy") first frame.
    tweens.forEach((tween) => tween.kill());
    clearTweened(targets);
    window.requestAnimationFrame(() => {
      clearTweened(targets);
      window.requestAnimationFrame(() => clearTweened(targets));
    });
    return targets.length;
  };
  const drawerOpen = () => mobileQuery.matches && search.classList.contains('is-filter-panel-open');

  // "Rs. 100,000 - Rs. 200,000" → "Rs. 100,000 – 200,000": one currency mark per range and an
  // en dash, so the presets fit the column on one line (also "$1,000 – 2,000", "1 000 – 2 000 kr").
  const tidyRange = (text) => {
    const parts = String(text || '').split(' - ');
    if (parts.length !== 2) return text;
    let [low, high] = parts.map((part) => part.trim());
    const sample = shopperCurrency !== shopCurrency && shopperMoney ? shopperMoney.format(1) : '$1';
    const digit = sample.search(/\d/);
    const prefix = sample.slice(0, digit).trim();
    const suffix = sample.slice(digit + 1).trim();
    if (prefix && low.startsWith(prefix) && high.startsWith(prefix)) high = high.slice(prefix.length).trim();
    if (suffix && low.endsWith(suffix) && high.endsWith(suffix)) low = low.slice(0, -suffix.length).trim();
    return `${low} – ${high}`;
  };

  /* 1. Cards ----------------------------------------------------------------- */

  const CARD_WIDTHS = [320, 480, 640, 800, 960];
  const GALLERY_WIDTHS = [320, 480, 640, 720];
  const CARD_SIZES = '(min-width: 1200px) 23vw, (min-width: 990px) 25vw, (min-width: 640px) 33vw, 50vw';

  // Liquid cards load images from the shop domain (//shop/cdn/shop/files/...).
  // Search results use cdn.shopify.com originals (1600px). Mapping them onto the
  // same sized URLs lets the browser reuse what the first page already loaded.
  const cdnBase = (() => {
    const img = document.querySelector('.product-card__image[srcset*="/cdn/shop/files/"]');
    const first = img && img.getAttribute('srcset').split(',')[0].trim().split(/\s+/)[0];
    const match = first && first.match(/^(.*\/cdn\/shop\/)files\//);
    return match ? match[1] : '';
  })();

  // The sized URLs of one photo, worked out once: a card repeats its photos (the
  // first slide is the main photo, and the gallery loops over clones), and parsing
  // a URL per width per image was most of the time spent preparing a page of cards.
  const sizedSets = new Map();
  const sizedSet = (src, widths) => {
    const key = `${widths.join(',')}|${src}`;
    let set = sizedSets.get(key);
    if (set) return set;
    set = null;
    try {
      let raw = src;
      const match = raw.match(/^https?:\/\/cdn\.shopify\.com\/s\/files\/(?:\d+\/){4}(files\/.+)$/);
      if (match && cdnBase) raw = cdnBase + match[1];
      const url = new URL(raw, window.location.href);
      if (/cdn\.shopify\.com|\/cdn\/shop\//.test(url.href)) {
        const urls = widths.map((width) => {
          url.searchParams.set('width', String(width));
          return url.href;
        });
        set = { src: urls[urls.length - 1], srcset: urls.map((href, index) => `${href} ${widths[index]}w`).join(', ') };
      }
    } catch (_error) {
      set = null;
    }
    if (sizedSets.size > 800) sizedSets.clear();
    sizedSets.set(key, set);
    return set;
  };

  // srcset + sizes pick the photo (src is only the fallback for browsers without srcset,
  // so it is left as it is: one attribute write less per photo, eight photos per card).
  const sizeImage = (img, widths, sizes) => {
    const src = img.getAttribute('src');
    if (!src || img.hasAttribute('srcset') || /[?&]width=/.test(src)) return;
    const set = sizedSet(src, widths);
    if (!set || set.src === src) return;
    img.setAttribute('sizes', sizes);
    img.setAttribute('srcset', set.srcset);
  };

  const watchMedia = (card) => {
    const done = () => card.classList.add('is-media-loaded');
    const images = [
      card.querySelector('.product-card__image--primary'),
      card.querySelector('[data-gallery-slide]:not([data-gallery-clone])')
    ].filter(Boolean);
    if (!images.length) return done();
    if (images.some((img) => img.complete && img.naturalWidth > 0)) return done();
    if (images.every((img) => img.complete && img.getAttribute('loading') !== 'lazy')) return done();
    images.forEach((img) => {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  };

  // Search cards print "Retail: $X". Liquid cards print "Est. Retail $X" plus a
  // "% below retail" badge. Use the Liquid version everywhere.
  // The search component prints a piece's retail price as "Retail: …", in the shop's currency
  // and never converted. A card shows only how far below retail the piece is (the product page
  // has the amount): a percentage reads the same in every currency. Worked out from the piece's
  // own price in the shop's currency (the printed price may hold a sale's old price too).
  const normalizeRetail = (card) => {
    const retail = card.querySelector('p.product-card__retail');
    if (!retail) return;
    const retailValue = toNumber(retail.textContent);
    const price = Number(card.querySelector('[data-product-price]')?.dataset.productPrice) || 0;
    const below = retailValue > price && price > 0 ? Math.round(((retailValue - price) * 100) / retailValue) : 0;
    if (below < 1) {
      retail.remove();
      return;
    }
    const line = document.createElement('div');
    line.className = 'fp-retail-discount';
    const badge = document.createElement('span');
    badge.className = 'ldj-retail-badge';
    badge.textContent = `${below}% below retail`;
    line.append(badge);
    retail.replaceWith(line);
  };

  const normalizeCondition = (card) => {
    const label = card.querySelector('.product-card__condition-label:not(.product-card__condition-label--mobile):not(.product-card__condition-label--desktop)');
    if (!label) return;
    const short = document.createElement('span');
    short.className = 'product-card__condition-label product-card__condition-label--mobile';
    short.textContent = 'Cond: ';
    label.classList.add('product-card__condition-label--desktop');
    label.before(short);
  };

  const prepareCard = (card, serverRendered = false) => {
    if (card.dataset.plpCard !== undefined || card.classList.contains('product-card--skeleton')) return;
    card.dataset.plpCard = '';
    card.querySelectorAll('.product-card__image').forEach((img) => sizeImage(img, CARD_WIDTHS, CARD_SIZES));
    card.querySelectorAll('[data-gallery-slide]').forEach((img) => sizeImage(img, GALLERY_WIDTHS, '50vw'));
    normalizeRetail(card);
    normalizeCondition(card);
    priceInShopperCurrency(card);
    // In the SOLD collection every piece has sold: "Sold", not "Sold out".
    if (soldArchive) {
      card.querySelectorAll('.product-card__badges .badge').forEach((badge) => {
        if (/^sold out$/i.test(badge.textContent.trim())) badge.textContent = 'Sold';
      });
    }
    // A piece that has sold shows no price on any card (LDJ, 2026-09-30); collection.css hides
    // the amount, the Sale tag and the % below retail on cards marked here.
    const sold = soldArchive || [...card.querySelectorAll('.product-card__badges .badge')]
      .some((badge) => /^sold( out)?$/i.test(badge.textContent.trim()));
    card.classList.toggle('is-sold', sold);
    const vendor = card.querySelector('.product-card__vendor');
    const spelled = vendor && houseName(vendor.textContent);
    if (spelled && vendor.textContent !== spelled) vendor.textContent = spelled;

    // Cards already on screen stay visible: the server-rendered first page, and
    // the identical search cards that replace it (same image URLs, so already
    // cached) without the fade-in.
    if (serverRendered) {
      card.classList.add('is-media-loaded');
      return;
    }
    if (search.classList.contains('is-hydrating')) {
      card.classList.add('is-media-loaded', 'plp-no-fade');
      window.setTimeout(() => card.classList.remove('plp-no-fade'), 1500);
      return;
    }
    watchMedia(card);
  };

  const scanCards = (root) => {
    if (root.matches?.('[data-product-card]')) prepareCard(root);
    root.querySelectorAll?.('[data-product-card]').forEach((card) => prepareCard(card));
  };

  /* 2 + 3. Filters ------------------------------------------------------------ */

  const FACET_ORDER = ['designer', 'price', 'condition', 'color', 'material', 'hardware', 'category', 'type', 'includes', 'location', 'availability'];
  const DEFAULT_OPEN = new Set(['designer', 'price', 'condition']);
  const SEARCHABLE_MIN = 12;
  const PRICE_ORDER = ['Under1000', 'From1000To2000', 'From2000To5000', 'From5000To8000', 'Over8000'];
  // Groups in a set order rather than by how many pieces each option has: LDJ's condition
  // scale best first (the product page's scale, read left to right), and Box before Dust Bag.
  const OPTION_ORDER = {
    condition: ['pristine', 'excellent', 'great', 'very good', 'good'],
    includes: ['box', 'dust bag']
  };
  const LABELS = {
    availability: { in_stock: 'Available Now', out_of_stock: 'Sold' }
  };
  const SWATCHES = {
    black: '#141414', white: '#ffffff', ivory: '#f6f1e4', cream: '#f3ead8', beige: '#d8c6a5', tan: '#c19a6b',
    brown: '#6b4a2f', camel: '#b8864b', grey: '#9b9b9b', gray: '#9b9b9b', blue: '#2f5aa8', navy: '#1f2a44',
    green: '#3f6b45', khaki: '#8a8558', red: '#b3261e', burgundy: '#6d1f2e', pink: '#e7a3b8', purple: '#6b4a8a',
    yellow: '#e9c93c', orange: '#e07b39', gold: '#c9a54a', silver: '#c7c7c7', bronze: '#8c6239',
    'aged gold': '#a8873f', 'pale gold': '#d8c07a', 'light gold': '#e0c77f', 'rose gold': '#d4a18f',
    palladium: '#bdbdbd', ruthenium: '#4d4d4d', gunmetal: '#53565a',
    metallic: 'linear-gradient(135deg, #e6e6e6, #9d9d9d 45%, #e9d8a6)',
    multicolor: 'conic-gradient(#e0533d, #e9c93c, #3f9b5b, #2f5aa8, #8a4fb0, #e0533d)',
    tonal: 'linear-gradient(135deg, #d8c6a5 50%, #6b4a2f 50%)',
    mixed: 'linear-gradient(135deg, #c9a54a 50%, #c7c7c7 50%)'
  };
  const SWATCH_PARAMS = new Set(['color', 'hardware']);

  const paramOf = (facet) => facet.querySelector('[data-filter-input]')?.dataset.filterParam || '';
  const labelOf = (facet) => facet.querySelector('summary > span')?.textContent.trim() || '';
  // House spellings (the catalogue says "Hermes", "Chloe"), as on the cards
  const HOUSE_NAMES = { hermes: 'Hermès', chloe: 'Chloé' };
  const houseName = (name) => HOUSE_NAMES[fold(name)] || null;
  const displayLabel = (param, value) => {
    if (LABELS[param]?.[value]) return LABELS[param][value];
    if (param === 'designer' || param === 'brand') return houseName(value);
    if (param === 'location') return String(value).replace(/^luxe du jour\s+/i, '');
    return null;
  };

  const openState = new Map();
  const expanded = new Set();
  const queries = new Map();

  const totalFound = () => toNumber(count?.textContent);

  const expandFacet = (facet) => {
    const more = facet.querySelector('[data-facet-more]');
    if (!more || more.getAttribute('aria-expanded') === 'true') return;
    facet.querySelectorAll('[data-facet-extra]').forEach((option) => { option.hidden = false; });
    facet.classList.add('is-expanded');
    more.setAttribute('aria-expanded', 'true');
    more.textContent = more.dataset.lessLabel || 'Show Less';
  };

  // Colour and hardware swatches, painted only into an open group: closed groups are
  // not drawn, and the fifty-odd writes per render were re-serialized by Shopify's pixels.
  const decorateSwatches = (facet) => {
    if (!facet || facet.dataset.plpSwatched !== undefined || !SWATCH_PARAMS.has(facet.dataset.plpParam)) return;
    facet.dataset.plpSwatched = '';
    facet.querySelectorAll('.luxe-search__facet-option').forEach((option) => {
      const value = option.querySelector('[data-filter-input]')?.dataset.filterValue || '';
      const swatch = SWATCHES[fold(value)] || SWATCHES[fold(value.split(',')[0])];
      if (!swatch) return;
      option.style.setProperty('--plp-swatch', swatch);
      option.classList.add('has-swatch');
      if (fold(value) === 'white' || fold(value) === 'ivory' || fold(value) === 'cream') option.classList.add('has-swatch--light');
    });
  };

  const decorateOptions = (facet, param) => {
    const seen = new Set();
    facet.querySelectorAll('.luxe-search__facet-option').forEach((option) => {
      const input = option.querySelector('[data-filter-input]');
      const text = option.querySelector('span');
      if (!input || !text) return;
      const value = input.dataset.filterValue || '';
      const key = fold(value);
      if (seen.has(key) && !input.checked && option.dataset.plpDuplicate === undefined) option.dataset.plpDuplicate = '';
      seen.add(key);
      const pretty = displayLabel(param, value);
      if (pretty && text.textContent !== pretty) text.textContent = pretty;
      // Counts read like the results count above them: "1,323", not "1323"
      const count = option.lastElementChild;
      if (count !== text && /^\d{4,}$/.test(count.textContent)) count.textContent = wholeNumber.format(Number(count.textContent));
    });

    if (param === 'price') {
      const box = facet.querySelector('.luxe-search__facet-options');
      const options = Array.from(box.querySelectorAll('.luxe-search__facet-option'));
      const sorted = options.slice()
        .sort((a, b) => PRICE_ORDER.indexOf(a.querySelector('input').dataset.filterValue) - PRICE_ORDER.indexOf(b.querySelector('input').dataset.filterValue));
      sorted.forEach((option) => {
        if (option.hidden) option.hidden = false;
        if (option.hasAttribute('data-facet-extra')) option.removeAttribute('data-facet-extra');
        if (PRICE_RANGE_VALUES.has(option.querySelector('input')?.dataset.filterValue)) {
          const text = option.querySelector('label > span:not(:last-child), .luxe-search__facet-label') || option.querySelector('span');
          labelPriceRange(text);
          const tidy = text && tidyRange(text.textContent.trim());
          if (text && text.textContent !== tidy) text.textContent = tidy;
        }
      });
      placeInOrder(box, options, sorted);
      facet.querySelector('[data-facet-more]')?.remove();
      addPriceRange(box);
    }

    // Conditions read down the grading scale (Pristine … Good), Includes as Box then Dust Bag:
    // counts would reorder them, and move the options on every pick.
    const order = OPTION_ORDER[param];
    if (order) {
      const box = facet.querySelector('.luxe-search__facet-options');
      const options = Array.from(box.querySelectorAll('.luxe-search__facet-option'));
      const rank = (option) => {
        const index = order.indexOf(fold(option.querySelector('input')?.dataset.filterValue));
        return index < 0 ? order.length : index;
      };
      placeInOrder(box, options, options.slice().sort((a, b) => rank(a) - rank(b)));
    }
  };

  /* Own price range: min–max fields under the presets. The component keeps price_min /
     price_max in the address (in the presets' units: the shopper's currency ÷ priceScale),
     so the fields write them there and hand over with a popstate, which it already handles. */

  const moneyAffixes = (() => {
    const sample = shopperCurrency !== shopCurrency && shopperMoney ? shopperMoney.format(1) : '$1';
    const first = sample.search(/\d/);
    return { prefix: sample.slice(0, first).trim(), suffix: sample.slice(first + 1).trim() };
  })();

  const priceParams = () => {
    const params = new URLSearchParams(window.location.search);
    return { min: params.get('price_min') || '', max: params.get('price_max') || '' };
  };

  const applyPriceRange = (min, max) => {
    const url = new URL(window.location.href);
    ['price_min', 'price_max', 'page'].forEach((key) => url.searchParams.delete(key));
    if (min) url.searchParams.set('price_min', String(min));
    if (max) url.searchParams.set('price_max', String(max));
    if (url.href === window.location.href) return false;
    window.history.pushState(window.history.state, '', url);
    window.dispatchEvent(new PopStateEvent('popstate', { state: window.history.state }));
    return true;
  };

  const readRange = (form) => {
    const scale = priceScale();
    const value = (name) => {
      const number = Number(String(form.elements[name]?.value || '').replace(/[^\d.]/g, ''));
      return Number.isFinite(number) && number > 0 ? Math.max(1, Math.round(number / scale)) : '';
    };
    let min = value('min');
    let max = value('max');
    if (min && max && min > max) [min, max] = [max, min];
    return { min, max };
  };

  function addPriceRange(box) {
    if (!box) return;
    // Refreshes re-append the presets: keep the fields under them.
    const existing = box.querySelector('.plp-price-range');
    if (existing) {
      if (box.lastElementChild !== existing) box.append(existing);
      return;
    }
    const scale = priceScale();
    // An own range fills the fields; a preset (checked above) leaves them empty.
    const presetChecked = box.querySelector('[data-filter-input][data-filter-param="price"]:checked');
    const current = presetChecked ? { min: '', max: '' } : {
      min: search.state?.priceMin ?? priceParams().min,
      max: search.state?.priceMax ?? priceParams().max
    };
    const form = document.createElement('form');
    form.className = 'plp-price-range';
    form.noValidate = true;
    form.innerHTML = `
      <p class="plp-price-range__title">Or Set Your Own Range</p>
      <div class="plp-price-range__row">
        <label class="plp-price-range__field"><span class="plp-price-range__prefix"></span><input name="min" type="text" inputmode="numeric" autocomplete="off" placeholder="Min" aria-label="Minimum price"></label>
        <span class="plp-price-range__dash" aria-hidden="true">–</span>
        <label class="plp-price-range__field"><span class="plp-price-range__prefix"></span><input name="max" type="text" inputmode="numeric" autocomplete="off" placeholder="Max" aria-label="Maximum price"></label>
        <button class="plp-price-range__apply" type="submit">Apply</button>
      </div>`;
    form.querySelectorAll('.plp-price-range__prefix').forEach((prefix) => {
      prefix.textContent = moneyAffixes.prefix || moneyAffixes.suffix;
    });
    const group = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
    if (current.min) form.elements.min.value = group.format(Number(current.min) * scale);
    if (current.max) form.elements.max.value = group.format(Number(current.max) * scale);
    // Typed but not applied: the sheet puts the fields back when it closes (theme.js).
    form.addEventListener('input', () => {
      if (drawerOpen()) search.facetDraftDirty = true;
    });
    form.addEventListener('submit', (event) => {
      event.preventDefault();
      const { min, max } = readRange(form);
      applyPriceRange(min, max);
      if (drawerOpen()) search.querySelector('[data-filter-close]')?.click();
      scrollToResults();
    });
    box.append(form);
  }

  // Phones: "Show products" applies the sheet's checkboxes, which only know the presets, so
  // an own range (typed, or already in the address) is put back once the component is done.
  search.querySelector('[data-filter-apply]')?.addEventListener('click', () => {
    const form = facets?.querySelector('.plp-price-range');
    const presetChecked = facets?.querySelector('[data-filter-input][data-filter-param="price"]:checked');
    if (!form || presetChecked) return;
    const { min, max } = readRange(form);
    if (!min && !max) return;
    window.setTimeout(() => applyPriceRange(min, max), 60);
  });

  const addFacetSearch = (facet, label) => {
    const box = facet.querySelector('.luxe-search__facet-options');
    const options = Array.from(facet.querySelectorAll('.luxe-search__facet-option'));
    if (!box || options.length < SEARCHABLE_MIN) {
      queries.delete(label);
      return;
    }
    if (box.querySelector('.plp-facet-search')) return;

    const field = document.createElement('div');
    field.className = 'plp-facet-search';
    field.innerHTML = '<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><circle cx="7" cy="7" r="4.75"/><path d="M10.5 10.5 14 14" stroke-linecap="round"/></svg><input type="search" autocomplete="off" spellcheck="false">';
    const input = field.querySelector('input');
    input.placeholder = `Search ${label.toLowerCase()}`;
    input.setAttribute('aria-label', `Search ${label.toLowerCase()}`);
    const empty = document.createElement('p');
    empty.className = 'plp-facet-empty';
    empty.hidden = true;
    empty.textContent = 'No matches';
    box.prepend(field);
    box.append(empty);

    const apply = () => {
      const query = fold(input.value);
      queries.set(label, input.value);
      let shown = 0;
      const show = (option, visible) => {
        if (option.hidden === visible) option.hidden = !visible;
      };
      options.forEach((option) => {
        if (!query) {
          show(option, !(option.hasAttribute('data-facet-extra') && !facet.classList.contains('is-expanded')));
          return;
        }
        const hit = fold(option.querySelector('span')?.textContent).includes(query);
        show(option, hit);
        if (hit) shown += 1;
      });
      const more = facet.querySelector('[data-facet-more]');
      if (more && more.hidden !== Boolean(query)) more.hidden = Boolean(query);
      if (empty.hidden !== (!query || shown > 0)) empty.hidden = !query || shown > 0;
    };

    input.addEventListener('input', apply);
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') event.preventDefault();
      if (event.key === 'Escape' && input.value) {
        event.preventDefault();
        event.stopPropagation();
        input.value = '';
        apply();
      }
    });
    if (queries.get(label)) {
      input.value = queries.get(label);
      apply();
    }
  };

  // A closed group says what is chosen in it ("Excellent, Pristine") under its name, so the
  // column reads at a glance without opening every group (collection.css hides it when open).
  const wholeNumber = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const ownRangeLabel = () => {
    const { min, max } = priceParams();
    if (!min && !max) return '';
    const amount = (value) => (shopperCurrency !== shopCurrency && shopperMoney
      ? shopperMoney.format(Number(value) * priceScale())
      : `$${wholeNumber.format(Number(value))}`);
    if (min && max) return tidyRange(`${amount(min)} - ${amount(max)}`);
    return min ? `${amount(min)}+` : `Under ${amount(max)}`;
  };

  const setPicked = (facet, param, inputs) => {
    const summary = facet.querySelector('summary');
    if (!summary) return;
    const names = inputs
      .filter((input) => input.checked)
      .map((input) => input.closest('label')?.querySelector('span')?.textContent.trim())
      .filter(Boolean);
    if (param === 'price' && !names.length) {
      const own = ownRangeLabel();
      if (own) names.push(own);
    }
    let line = summary.querySelector('.plp-facet-picked');
    if (!names.length) {
      line?.remove();
      return;
    }
    if (!line) {
      line = document.createElement('span');
      line.className = 'plp-facet-picked';
      summary.append(line);
    }
    const text = names.join(', ');
    if (line.textContent !== text) line.textContent = text;
  };

  // Put `items` (children of `parent`) in the `wanted` order with as few moves as possible:
  // the longest run already in order stays, the rest are slotted in. Every moved subtree is
  // laid out again and re-serialized by Shopify's pixel manager.
  function placeInOrder(parent, items, wanted) {
    if (items.every((item, index) => item === wanted[index])) return;
    const position = new Map(wanted.map((item, index) => [item, index]));
    const sequence = items.map((item) => position.get(item));
    const length = sequence.map(() => 1);
    const previous = sequence.map(() => -1);
    let best = 0;
    for (let i = 0; i < sequence.length; i += 1) {
      for (let j = 0; j < i; j += 1) {
        if (sequence[j] < sequence[i] && length[j] + 1 > length[i]) {
          length[i] = length[j] + 1;
          previous[i] = j;
        }
      }
      if (length[i] > length[best]) best = i;
    }
    const stay = new Set();
    for (let i = best; i >= 0; i = previous[i]) stay.add(items[i]);
    let next = items[items.length - 1].nextSibling;
    for (let i = wanted.length - 1; i >= 0; i -= 1) {
      const item = wanted[i];
      if (!stay.has(item)) parent.insertBefore(item, next);
      next = item;
    }
  }

  const refineFacets = () => {
    if (!facets || facets.querySelector('.luxe-search__facet--skeleton')) return;
    const groups = Array.from(facets.querySelectorAll('details.luxe-search__facet'));
    if (!groups.length) return;
    const found = totalFound();

    const rank = (facet) => {
      const index = FACET_ORDER.indexOf(paramOf(facet));
      return index < 0 ? 99 : index;
    };
    const ordered = groups.slice().sort((a, b) => rank(a) - rank(b));
    placeInOrder(facets, groups, ordered);

    groups.forEach((facet) => {
      const param = paramOf(facet);
      const label = labelOf(facet);
      if (facet.dataset.plpParam !== param) facet.dataset.plpParam = param;
      decorateOptions(facet, param);

      const inputs = Array.from(facet.querySelectorAll('[data-filter-input]'));
      const selected = inputs.filter((input) => input.checked).length;
      const badge = facet.querySelector('.luxe-search__facet-count');
      const badgeText = selected ? String(selected) : '';
      if (badge && badge.textContent !== badgeText) badge.textContent = badgeText;
      facet.classList.toggle('has-selection', selected > 0);
      setPicked(facet, param, inputs);

      // A group where every product shares the one value can't narrow anything, and the SOLD
      // collection shows no prices, so it has no price group (kept while a link still has a
      // price picked, so it can be cleared). Muting also leaves theme.js's facet animation:
      // GSAP measures a hidden element by moving it to <html> and back, which set off this
      // whole refresh again on every sheet open.
      const onlyOption = inputs.length === 1 ? inputs[0] : null;
      const counted = onlyOption ? toNumber(onlyOption.closest('label')?.lastElementChild?.textContent) : 0;
      if ((soldArchive && param === 'price' && !selected) || (onlyOption && !onlyOption.checked && found && counted >= found)) {
        facet.hidden = true;
        facet.classList.replace('luxe-search__facet', 'plp-facet-muted');
        return;
      }

      const open = openState.has(label) ? openState.get(label) : DEFAULT_OPEN.has(param) || selected > 0;
      if (facet.open !== open) facet.open = open;
      if (open) decorateSwatches(facet);
      if (expanded.has(label)) expandFacet(facet);
      if (facet.querySelector('[data-facet-extra][hidden] input:checked')) expandFacet(facet);
      addFacetSearch(facet, label);
    });
  };

  if (facets) {
    // In the sheet, choices are drafts (no redraw until "Show products"): keep each group's
    // summary line and count in step with its boxes as they change.
    facets.addEventListener('change', (event) => {
      const facet = event.target.closest?.('details.luxe-search__facet');
      if (!facet || !event.target.matches('[data-filter-input]')) return;
      const inputs = Array.from(facet.querySelectorAll('[data-filter-input]'));
      const selected = inputs.filter((input) => input.checked).length;
      const badge = facet.querySelector('.luxe-search__facet-count');
      const badgeText = selected ? String(selected) : '';
      if (badge && badge.textContent !== badgeText) badge.textContent = badgeText;
      facet.classList.toggle('has-selection', selected > 0);
      setPicked(facet, facet.dataset.plpParam || paramOf(facet), inputs);
    });

    facets.addEventListener('toggle', (event) => {
      const facet = event.target;
      if (!(facet instanceof HTMLDetailsElement) || !facet.classList.contains('luxe-search__facet')) return;
      openState.set(labelOf(facet), facet.open);
      if (facet.open) decorateSwatches(facet);
    }, true);

    // A closed group gets its swatches just before it opens, so its first frame has them.
    facets.addEventListener('click', (event) => {
      const summary = event.target.closest('summary');
      const facet = summary && summary.parentElement;
      if (facet instanceof HTMLDetailsElement && !facet.open) decorateSwatches(facet);
    }, true);

    facets.addEventListener('click', (event) => {
      const more = event.target.closest('[data-facet-more]');
      if (!more) return;
      const facet = more.closest('.luxe-search__facet');
      window.setTimeout(() => {
        if (more.getAttribute('aria-expanded') === 'true') expanded.add(labelOf(facet));
        else expanded.delete(labelOf(facet));
      });
    });
  }

  /* While a search runs, theme.js swaps the list for a skeleton (on desktop: every click). The
     list you were using goes back in its place instead, counts faded until the answer comes:
     nothing jumps, the column keeps its scroll, and a second choice can follow at once
     (theme.js keeps its own state, not this list). The first load keeps the skeleton.
     Keyboard users get the box they toggled back, focused, after the redraw. */
  let heldFocus = null;
  const scrollMemory = new Map();
  const facetScrollers = [search.querySelector('[data-filter-panel]'), facets].filter(Boolean);
  facetScrollers.forEach((scroller) => {
    scrollMemory.set(scroller, scroller.scrollTop);
    scroller.addEventListener('scroll', () => scrollMemory.set(scroller, scroller.scrollTop), { passive: true });
  });

  const filterInputFor = ({ param, value }) => facets?.querySelector(`[data-filter-input][data-filter-param="${escapeSelector(param)}"][data-filter-value="${escapeSelector(value)}"]`);

  search.addEventListener('change', (event) => {
    const input = event.target.closest?.('[data-filter-input]');
    if (!input || !facets?.contains(input) || drawerOpen()) return;
    heldFocus = document.activeElement === input ? { param: input.dataset.filterParam, value: input.dataset.filterValue } : null;
  }, true);

  function holdFacets(records) {
    if (!facets.querySelector('.luxe-search__facet--skeleton')) {
      facets.classList.remove('is-held');
      facets.removeAttribute('aria-busy');
      return false;
    }
    const previous = records
      .flatMap((record) => Array.from(record.removedNodes))
      .filter((node) => node.nodeType === 1 && node.matches('details.luxe-search__facet'));
    if (!previous.length) return false;
    facets.replaceChildren(...previous);
    facets.classList.add('is-held');
    facets.setAttribute('aria-busy', 'true');
    keepFacetScroll();
    if (heldFocus) filterInputFor(heldFocus)?.focus({ preventScroll: true });
    return true;
  }

  // The column (and the sheet's list) stay where the shopper left them across redraws.
  function keepFacetScroll() {
    facetScrollers.forEach((scroller) => {
      const top = scrollMemory.get(scroller) || 0;
      if (Math.abs(scroller.scrollTop - top) > 1) scroller.scrollTop = top;
    });
  }

  function restoreFacetFocus() {
    if (!heldFocus) return;
    const target = filterInputFor(heldFocus);
    heldFocus = null;
    const current = document.activeElement;
    if (target && (!current || current === document.body)) target.focus({ preventScroll: true });
  }

  // A failed search leaves the held list in place: live again once the component stops loading.
  new MutationObserver(() => {
    if (!search.classList.contains('is-loading') && facets?.classList.contains('is-held')) {
      facets.classList.remove('is-held');
      facets.removeAttribute('aria-busy');
    }
  }).observe(search, { attributes: true, attributeFilter: ['class'] });

  // Filter through the component: the real checkbox when it is rendered,
  // otherwise a stand-in that its change handler reads the same way.
  const setFilter = (param, value, on) => {
    if (!facets || !param || !value) return;
    const selector = `[data-filter-param="${escapeSelector(param)}"][data-filter-value="${escapeSelector(value)}"]`;
    const real = facets.querySelector(`[data-filter-input]${selector}`);
    if (real) {
      if (real.checked !== on) real.click();
      return;
    }
    if (!on) {
      const chip = active?.querySelector(`[data-remove-filter="${escapeSelector(param)}"][data-remove-value="${escapeSelector(value)}"]`);
      if (chip) {
        chip.click();
        return;
      }
    }
    const standIn = document.createElement('input');
    standIn.type = 'checkbox';
    standIn.hidden = true;
    standIn.setAttribute('data-filter-input', '');
    standIn.dataset.filterParam = param;
    standIn.dataset.filterValue = value;
    standIn.checked = on;
    facets.append(standIn);
    standIn.dispatchEvent(new Event('change', { bubbles: true }));
    standIn.remove();
  };

  const isActive = (param, value) => Boolean(active?.querySelector(`[data-remove-filter="${escapeSelector(param)}"][data-remove-value="${escapeSelector(value)}"]`));

  /* Quick chips */

  const QUICK_LIMIT = 9;
  let availabilityChipDone = false;
  let designerChipsDone = false;
  let styleChipsDone = false;

  const facetValues = (param) => Array.from(facets?.querySelectorAll(`[data-filter-input][data-filter-param="${escapeSelector(param)}"]`) || [])
    .map((input) => ({ value: input.dataset.filterValue || '', count: toNumber(input.closest('label')?.lastElementChild?.textContent) }))
    .filter((item) => item.value && item.count > 0);

  const chip = (param, value, label, extraClass = '') => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `plp-quick__chip${extraClass ? ` ${extraClass}` : ''}`;
    button.dataset.param = param;
    button.dataset.value = value;
    button.setAttribute('aria-pressed', 'false');
    button.textContent = label;
    return button;
  };

  // Built once from an unfiltered facet list, then kept stable: facet counts
  // narrow as filters apply, and the chips must not shrink with them.
  const buildQuick = () => {
    if (!quick || !facets || facets.querySelector('.luxe-search__facet--skeleton')) return;
    if (!facets.querySelector('[data-filter-input]')) return;
    let changed = false;

    if (!availabilityChipDone) {
      availabilityChipDone = true;
      const values = facetValues('availability').map((item) => item.value);
      if (isActive('availability', 'in_stock') || (values.includes('in_stock') && values.includes('out_of_stock'))) {
        quick.prepend(chip('availability', 'in_stock', LABELS.availability.in_stock, 'plp-quick__chip--toggle'));
        changed = true;
      }
    }

    if (!designerChipsDone && !active?.querySelector('[data-remove-filter="designer"]')) {
      const seen = new Set();
      const designers = facetValues('designer')
        .sort((a, b) => b.count - a.count)
        .filter((item) => !seen.has(fold(item.value)) && seen.add(fold(item.value)));
      if (designers.length >= 3) {
        designerChipsDone = true;
        designers.slice(0, QUICK_LIMIT).forEach((item) => quick.append(chip('designer', item.value, houseName(item.value) || item.value)));
        changed = true;
      }
    }

    // One house (a designer's own page): its top colours and leathers instead.
    const houses = facetValues('designer').length;
    if (!designerChipsDone && !styleChipsDone && houses > 0 && houses < 3
      && !active?.querySelector('[data-remove-filter="color"], [data-remove-filter="material"]')) {
      const found = totalFound();
      const top = (param, limit) => facetValues(param)
        .filter((item) => item.count >= 3 && (!found || item.count < found))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
      const colours = top('color', 5);
      const leathers = top('material', 3);
      if (colours.length + leathers.length >= 3) {
        styleChipsDone = true;
        colours.forEach((item) => {
          const button = chip('color', item.value, item.value);
          const swatch = SWATCHES[fold(item.value)];
          if (swatch) {
            const dot = document.createElement('span');
            dot.className = 'plp-quick__swatch';
            dot.setAttribute('aria-hidden', 'true');
            dot.style.setProperty('--plp-swatch', swatch);
            button.prepend(dot);
          }
          quick.append(button);
        });
        leathers.forEach((item) => quick.append(chip('material', item.value, item.value)));
        changed = true;
      }
    }

    if (changed) {
      quick.hidden = !quick.children.length;
      syncQuick();
      placeQuick();
    }
  };

  // Desktop: the chips sit in the toolbar, between the count and the sort (so they stay in
  // reach while it sticks); phones keep them under the title, and the toolbar compact.
  const quickSlot = search.querySelector('[data-plp-quick-slot]');
  const quickHome = quick && quickSlot ? document.createComment('plp-quick') : null;
  if (quickHome) quick.before(quickHome);
  function placeQuick() {
    if (!quickHome) return;
    const desktop = !mobileQuery.matches;
    if (desktop && quick.parentElement !== quickSlot) quickSlot.append(quick);
    if (!desktop && quick.parentElement === quickSlot) quickHome.after(quick);
    toolbar?.classList.toggle('has-quick', desktop && !quick.hidden && quick.children.length > 0);
    markScrollable();
  }
  // The edges fade only when the chips really scroll.
  function markScrollable() {
    if (quick) quick.classList.toggle('is-scrollable', quick.scrollWidth > quick.clientWidth + 1);
  }
  mobileQuery.addEventListener?.('change', placeQuick);
  window.addEventListener('resize', markScrollable, { passive: true });

  function syncQuick() {
    quick?.querySelectorAll('.plp-quick__chip').forEach((button) => {
      const pressed = String(isActive(button.dataset.param, button.dataset.value));
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
  }

  quick?.addEventListener('click', (event) => {
    const button = event.target.closest('.plp-quick__chip');
    if (!button) return;
    const on = button.getAttribute('aria-pressed') !== 'true';
    button.setAttribute('aria-pressed', String(on));
    setFilter(button.dataset.param, button.dataset.value, on);
    scrollToResults();
  });

  /* Active chips + Clear all */

  const refineActive = () => {
    if (!active) return;
    const chips = Array.from(active.querySelectorAll('[data-remove-filter]'));
    chips.forEach((button) => {
      const pretty = displayLabel(button.dataset.removeFilter, button.dataset.removeValue);
      const text = Array.from(button.childNodes).find((node) => node.nodeType === Node.TEXT_NODE && node.textContent.trim());
      if (pretty && text && text.textContent !== `${pretty} `) text.textContent = `${pretty} `;
      // Price chips hold amounts in the presets' units: written in the shopper's currency.
      if (text && /^price/.test(button.dataset.removeFilter || '') && shopperCurrency !== shopCurrency && !button.dataset.plpCurrency && text.textContent.includes('$')) {
        button.dataset.plpCurrency = '';
        text.textContent = `${shopperPriceText(text.textContent.trim())} `;
      }
      if (text && /^price/.test(button.dataset.removeFilter || '')) {
        const tidy = `${tidyRange(text.textContent.trim())} `;
        if (text.textContent !== tidy) text.textContent = tidy;
      }
      const ariaLabel = `Remove filter ${button.textContent.replace('×', '').trim()}`;
      if (button.getAttribute('aria-label') !== ariaLabel) button.setAttribute('aria-label', ariaLabel);
    });
    if (clearAll && clearAll.hidden !== chips.length < 2) clearAll.hidden = chips.length < 2;
    syncQuick();
    addEmptyAction();
  };

  // Desktop has no "clear everything" in the component: uncheck every box,
  // then apply, which searches once with no filters.
  const clearAllFilters = () => {
    search.querySelector('[data-filter-clear]')?.click();
    search.querySelector('[data-filter-apply]')?.click();
    scrollToResults();
  };
  clearAll?.addEventListener('click', clearAllFilters);

  const addEmptyAction = () => {
    const empty = results?.querySelector('.luxe-search__empty');
    if (!empty || empty.querySelector('.plp-empty-clear') || !active?.querySelector('[data-remove-filter]')) return;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'plp-empty-clear';
    button.textContent = 'Clear All Filters';
    button.addEventListener('click', clearAllFilters);
    empty.append(button);
  };

  /* 4. Toolbar ------------------------------------------------------------------ */

  // The toolbar's height, from its ResizeObserver (read after layout, so it never forces
  // one), written only when it changes: a custom property on the shell restyles the page.
  let toolbarHeight = -1;
  function measureToolbar(height) {
    if (!toolbar) return;
    const next = Math.round(height ?? toolbar.getBoundingClientRect().height);
    if (next === toolbarHeight) return;
    toolbarHeight = next;
    shell.style.setProperty('--plp-toolbar-h', `${next}px`);
  }

  // Desktop: the filter column scrolls on its own. Before it sticks it starts lower on the
  // screen, so its height follows where it starts (collection.css), keeping every option,
  // Location included, reachable with all groups open. The value lives on the column itself
  // (registered as non-inherited in collection.css) and is written only when it changes:
  // on the shell it restyled every card on each scroll frame.
  const filterPanel = search.querySelector('[data-filter-panel]');
  let filtersTop = -1;
  function fitFilters() {
    if (!filterPanel || mobileQuery.matches) return;
    const top = Math.max(0, Math.round(filterPanel.getBoundingClientRect().top));
    if (top === filtersTop) return;
    filtersTop = top;
    filterPanel.style.setProperty('--plp-filters-top', `${top}px`);
  }
  let fitQueued = false;
  const queueFit = () => {
    if (fitQueued || mobileQuery.matches) return;
    fitQueued = true;
    window.requestAnimationFrame(() => {
      fitQueued = false;
      fitFilters();
    });
  };
  mobileQuery.addEventListener?.('change', () => {
    filtersTop = -1;
    queueFit();
  });

  const syncSort = () => {
    if (!sortSelect || !sortValue) return;
    const text = sortSelect.selectedOptions[0]?.textContent.trim() || '';
    if (sortValue.textContent !== text) sortValue.textContent = text;
  };
  sortSelect?.addEventListener('change', () => {
    syncSort();
    scrollToResults();
  });

  if (toolbar) {
    if ('ResizeObserver' in window) {
      new ResizeObserver((entries) => {
        const entry = entries[entries.length - 1];
        measureToolbar(entry?.borderBoxSize?.[0]?.blockSize);
        queueFit();
      }).observe(toolbar);
    } else {
      measureToolbar();
    }

    // Stuck or not: an IntersectionObserver reports when the toolbar's top edge reaches the
    // line it sticks to (the header's height once the announcement bar has scrolled away).
    // Nothing runs per scroll frame; the old handler read computed style and layout on each.
    const stuckLine = () => {
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
      return Number.isFinite(header) && header > 0 ? header : parseFloat(getComputedStyle(toolbar).top) || 0;
    };
    const updateStuck = (top, stickTop) => {
      toolbar.classList.toggle('is-stuck', window.scrollY > 0 && top <= stickTop + 1);
    };
    if ('IntersectionObserver' in window) {
      let observer = null;
      let line = -1;
      const watchStuck = () => {
        const next = Math.round(stuckLine());
        if (next === line && observer) return;
        line = next;
        observer?.disconnect();
        observer = new IntersectionObserver(([entry]) => {
          if (entry) updateStuck(entry.intersectionRatio < 1 ? entry.boundingClientRect.top : Infinity, line);
        }, { rootMargin: `${-(line + 1)}px 0px 0px 0px`, threshold: [1] });
        observer.observe(toolbar);
      };
      watchStuck();
      window.addEventListener('resize', watchStuck, { passive: true });
      // The header's height is published by header-extras.js, possibly after this runs.
      window.setTimeout(watchStuck, 1500);
    } else {
      let queued = false;
      window.addEventListener('scroll', () => {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(() => {
          queued = false;
          updateStuck(toolbar.getBoundingClientRect().top, parseFloat(getComputedStyle(toolbar).top) || 0);
        });
      }, { passive: true });
    }

    window.addEventListener('resize', queueFit, { passive: true });
    window.addEventListener('scroll', queueFit, { passive: true });
  }

  /* 5. Keep the grid in view when filters change ------------------------------- */

  function scrollToResults() {
    const anchor = layout || results;
    if (!anchor || drawerOpen()) return;
    // Where the page lands the header is stuck, so its bottom is its height.
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const offset = headerHeight + (toolbar ? toolbar.getBoundingClientRect().height : 0) + 12;
    const target = anchor.getBoundingClientRect().top + window.scrollY - offset;
    if (window.scrollY > target + 8) {
      window.scrollTo({ top: Math.max(0, target), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    }
  }

  facets?.addEventListener('change', (event) => {
    if (event.target.closest('[data-filter-input]') && !drawerOpen()) scrollToResults();
  });
  active?.addEventListener('click', (event) => {
    if (event.target.closest('[data-remove-filter]')) scrollToResults();
  });
  search.querySelector('[data-filter-apply]')?.addEventListener('click', () => {
    window.setTimeout(scrollToResults, 320);
  });

  // Mobile sheet: tapping the dimmed page closes it.
  search.addEventListener('click', (event) => {
    if (event.target === search && search.classList.contains('is-filter-panel-open')) {
      search.querySelector('[data-filter-close]')?.click();
    }
  });

  /* Phones + tablets: the sheet behaves as a dialog. Focus moves into it (the sheet itself, so
     a tap shows no ring; Tab goes on to Close), Tab stays inside, Escape closes it, and focus
     goes back to the Filter button. theme.js still opens and closes it; this only follows its
     class. */
  const filterToggle = search.querySelector('[data-filter-toggle]');
  const filterClose = search.querySelector('[data-filter-close]');
  let sheetOpen = false;
  new MutationObserver(() => {
    const open = drawerOpen();
    if (!filterPanel || open === sheetOpen) return;
    sheetOpen = open;
    if (open) {
      filterPanel.setAttribute('role', 'dialog');
      filterPanel.setAttribute('aria-modal', 'true');
      // The sheet itself takes focus (screen readers announce it; touch shows no ring); Tab goes on to Close.
      filterPanel.setAttribute('tabindex', '-1');
      filterPanel.focus({ preventScroll: true });
      return;
    }
    filterPanel.removeAttribute('role');
    filterPanel.removeAttribute('aria-modal');
    filterPanel.removeAttribute('tabindex');
    const current = document.activeElement;
    if (!current || current === document.body || filterPanel.contains(current)) filterToggle?.focus({ preventScroll: true });
  }).observe(search, { attributes: true, attributeFilter: ['class'] });

  filterPanel?.addEventListener('keydown', (event) => {
    if (!drawerOpen()) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      filterClose?.click();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = filterPanel.querySelectorAll('button, input, select, summary, a[href]');
    const usable = (item) => !item.disabled && item.getClientRects().length > 0;
    let first = null;
    let last = null;
    for (let index = 0; index < items.length && !first; index += 1) if (usable(items[index])) first = items[index];
    for (let index = items.length - 1; index >= 0 && !last; index -= 1) if (usable(items[index])) last = items[index];
    if (!first) return;
    if (event.shiftKey && (document.activeElement === first || document.activeElement === filterPanel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  /* 6. Description (and the edit you're on, scrolled into its row) --------------- */

  const currentEdit = shell.querySelector('[data-plp-shortcuts] [aria-current="page"]');
  const editRow = currentEdit?.closest('.plp-shortcuts__list');
  if (currentEdit && editRow && editRow.scrollWidth > editRow.clientWidth) {
    const item = currentEdit.parentElement;
    editRow.scrollLeft = Math.max(0, item.offsetLeft - (editRow.clientWidth - item.offsetWidth) / 2);
  }


  const description = shell.querySelector('[data-plp-description]');
  const descriptionToggle = shell.querySelector('[data-plp-description-toggle]');
  if (description && descriptionToggle) {
    const fits = () => description.scrollHeight - description.clientHeight <= 2;
    window.requestAnimationFrame(() => {
      if (description.dataset.clamped === 'true' && fits()) {
        description.dataset.clamped = 'false';
        descriptionToggle.hidden = true;
      }
    });
    descriptionToggle.addEventListener('click', () => {
      const open = description.dataset.clamped === 'true';
      description.dataset.clamped = String(!open);
      descriptionToggle.setAttribute('aria-expanded', String(open));
      descriptionToggle.textContent = open ? 'Read Less' : 'Read More';
    });
  }

  /* 7. Search page ----------------------------------------------------------- */

  function setUpSearchPage() {
    const CARET = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true" focusable="false"><path d="M18 9.00005C18 9.00005 13.5811 15 12 15C10.4188 15 6 9 6 9" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"/></svg>';
    const SUGGESTIONS = [
      ['Hermès', '/collections/hermes'],
      ['Chanel', '/collections/chanel'],
      ['Louis Vuitton', '/collections/louis-vuitton'],
      ['Christian Dior', '/collections/christian-dior'],
      ['New Arrivals', '/collections/new-arrivals']
    ];
    const input = search.querySelector('[data-search-input]');
    const heading = shell.querySelector('h1');
    const hero = heading && heading.parentElement;
    const currentQuery = () => {
      const typed = input ? input.value.trim() : '';
      if (typed) return typed;
      const fromUrl = (new URLSearchParams(window.location.search).get('q') || '').trim();
      return fromUrl === '*' ? '' : fromUrl;
    };

    // Heading: a breadcrumb and "Results for “…”", in the collection's style.
    const setHeading = () => {
      if (!heading) return;
      const query = currentQuery();
      const text = query ? `Results for “${query}”` : 'Search';
      if (heading.textContent !== text) heading.textContent = text;
    };
    // The page outline, as on collections: h1 (results) > h2 (the pieces) > h3 (each card).
    // search.liquid has no h2 above the grid, so the cards' h3 skipped a level.
    const resultsGrid = search.querySelector('[data-search-results]');
    if (resultsGrid && !resultsGrid.previousElementSibling?.matches('h2')) {
      const piecesHeading = document.createElement('h2');
      piecesHeading.className = 'sr-only';
      piecesHeading.textContent = 'Pieces';
      resultsGrid.before(piecesHeading);
    }
    if (hero && heading) {
      hero.classList.add('collection-hero', 'search-hero');
      heading.classList.add('collection-hero__title');
      const crumbs = document.createElement('nav');
      crumbs.className = 'collection-hero__crumbs';
      crumbs.setAttribute('aria-label', 'Breadcrumb');
      crumbs.innerHTML = '<a href="/">Home</a><span aria-hidden="true">/</span><span aria-current="page">Search</span>';
      hero.prepend(crumbs);
      setHeading();
    }

    // The search box moves up beside the heading, so the sticky toolbar holds
    // only the controls. Waiting for the component keeps its references.
    const form = search.querySelector('[data-search-form]');
    const toolbarEl = search.querySelector('[data-search-toolbar]');
    if (form && hero && window.customElements) {
      window.customElements.whenDefined('luxe-search').then(() => {
        hero.append(form);
        toolbarEl?.classList.add('luxe-search__toolbar--controls-only');
      });
    }

    // Sort: "Sort by" plus the current choice, as on collection pages.
    const sortControl = search.querySelector('.luxe-search__sort-control');
    const select = search.querySelector('[data-search-sort]');
    if (sortControl && select && !sortControl.querySelector('[data-sort-value]')) {
      Array.from(sortControl.childNodes).forEach((node) => {
        if (node !== select) node.remove();
      });
      sortControl.insertAdjacentHTML('afterbegin', `<span class="luxe-search__sort-label">Sort by</span><span class="luxe-search__sort-value" data-sort-value></span><span class="icon luxe-search__sort-caret" aria-hidden="true">${CARET}</span>`);
      const names = { relevance: 'Best Match', newest: 'Newest to Oldest', oldest: 'Oldest to Newest', price_asc: 'Price: Low to High', price_desc: 'Price: High to Low' };
      const sortInUrl = new URLSearchParams(window.location.search).get('sort');
      if (!select.querySelector('option[value="newest"]')) {
        select.querySelector('option[value="relevance"]')?.after(new Option('Newest to Oldest', 'newest'));
      }
      if (!select.querySelector('option[value="oldest"]')) {
        select.querySelector('option[value="newest"]')?.after(new Option('Oldest to Newest', 'oldest'));
      }
      if (sortInUrl === 'newest' || sortInUrl === 'oldest') select.value = sortInUrl;
      Array.from(select.options).forEach((option) => {
        if (names[option.value]) option.textContent = names[option.value];
        // No LDJ piece has a retail price yet, so "Discount" would sort nothing.
        if (option.value === 'discount_desc' && select.value !== 'discount_desc') option.remove();
      });
    }

    // Active filters get the collection's "Clear all" beside them.
    const activeFilters = search.querySelector('[data-active-filters]');
    if (activeFilters && !search.querySelector('[data-plp-clear-all]')) {
      const row = document.createElement('div');
      row.className = 'plp-active-row';
      activeFilters.replaceWith(row);
      row.append(activeFilters);
      // On top of the pieces, as on collection pages, so the sticky bar stays one row.
      search.querySelector('.luxe-search__main')?.prepend(row);
      const clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'plp-clear-all';
      clear.dataset.plpClearAll = '';
      clear.hidden = true;
      clear.textContent = 'Clear All';
      row.append(clear);
    }

    // Count: "1 result", "1,220 results" (the heading already names the query).
    const countEl = search.querySelector('[data-search-count]');
    if (countEl) {
      const tidyCount = () => {
        const match = countEl.textContent.trim().match(/^([\d,]+) (?:products?|results?)(?: for .*)?$/);
        if (match) {
          const next = `${match[1]} ${match[1] === '1' ? 'result' : 'results'}`;
          if (countEl.textContent !== next) countEl.textContent = next;
        }
        setHeading();
      };
      new MutationObserver(tidyCount).observe(countEl, { childList: true, characterData: true, subtree: true });
      tidyCount();
    }

    // Empty results: say what was searched and offer somewhere to go.
    const grid = search.querySelector('[data-search-results]');
    if (grid) {
      const suggest = () => {
        const found = grid.querySelector('.luxe-search__empty');
        if (!found || found.dataset.searchSuggest !== undefined) return;
        if (search.querySelector('[data-active-filters] [data-remove-filter]')) return;
        // The component writes a <p>; a <div> can hold the suggestions.
        const empty = document.createElement('div');
        empty.className = 'luxe-search__empty search-empty';
        empty.dataset.searchSuggest = '';
        const query = currentQuery();
        const title = document.createElement('p');
        title.className = 'search-empty__title';
        title.textContent = query ? `Nothing matches “${query}”` : 'Search for a designer, a model or an item number';
        const hint = document.createElement('p');
        hint.className = 'search-empty__hint';
        hint.textContent = 'Check the spelling, try a designer or a model, or search an item number.';
        const list = document.createElement('ul');
        list.className = 'search-empty__list';
        list.setAttribute('role', 'list');
        SUGGESTIONS.forEach(([label, url]) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.className = 'plp-quick__chip';
          link.href = url;
          link.textContent = label;
          item.append(link);
          list.append(item);
        });
        empty.append(title, hint, list);
        found.replaceWith(empty);
      };
      new MutationObserver(suggest).observe(grid, { childList: true });
      suggest();
    }
  }

  /* Wire up ------------------------------------------------------------------------ */

  if (results) {
    results.querySelectorAll('[data-product-card]').forEach((card) => prepareCard(card, true));
    const isCard = (node) => node.nodeType === 1 && node.matches('[data-product-card], .product-card--skeleton');
    new MutationObserver((records) => {
      const added = [];
      let replaced = false;
      records.forEach((record) => {
        record.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          scanCards(node);
          if (isCard(node)) added.push(node);
        });
        if (Array.from(record.removedNodes).some(isCard)) replaced = true;
      });
      // A new page of pieces (not more of them at the end, not the first-page swap): one fade.
      if (settleTweens(added) && replaced && !search.classList.contains('is-hydrating') && added.some((node) => node.matches('[data-product-card]'))) {
        results.classList.remove('plp-fresh');
        results.classList.add('plp-fresh');
      }
      syncSort();
    }).observe(results, { childList: true });
    results.addEventListener('animationend', (event) => {
      if (event.target === results) results.classList.remove('plp-fresh');
    });
  }

  if (facets) {
    // Only new facet markup needs refining. Groups that were already refined and are merely
    // moved (our own ordering, the held list going back, or GSAP measuring one) do not start
    // another pass.
    const isFresh = (node) => node.nodeType === 1 && node.dataset?.plpParam === undefined;
    const facetObserver = new MutationObserver((records) => {
      if (records.some((record) => Array.from(record.addedNodes).some(isFresh))) refreshFacets(records);
    });
    const facetGroups = () => Array.from(facets.querySelectorAll(':scope > .luxe-search__facet, :scope > .plp-facet-muted'));
    // Reordering groups mutates the container, so pause the observer meanwhile.
    const refreshFacets = (records = []) => {
      facetObserver.disconnect();
      let held = false;
      try {
        held = holdFacets(records);
        if (!held) {
          refineFacets();
          buildQuick();
        }
      } finally {
        facetObserver.observe(facets, { childList: true });
      }
      settleTweens(facetGroups());
      // The phone sheet starts the same stagger again 130 ms after it opens (setFiltersOpen).
      if (drawerOpen() && SETTLE_THEME_TWEENS) window.setTimeout(() => settleTweens(facetGroups()), 160);
      if (held) return;
      queueFit();
      keepFacetScroll();
      restoreFacetFocus();
    };
    refreshFacets();
  }

  if (active) {
    new MutationObserver(refineActive).observe(active, { childList: true });
    refineActive();
  }

  // ── 9. Loading more, without a stall ───────────────────────────────────────
  // The component loads the next page when its sentinel (under the grid) enters a band round
  // the screen: one IntersectionObserver entry per crossing. Two shoppers fell through on
  // /collections/all and saw the grid end over a still line:
  //  - one who flicked past the grid into the footer while a page was still loading (the
  //    sentinel's entry came while the component was busy, and none came again);
  //  - one parked at the foot of the page: scroll anchoring kept the footer on screen while
  //    pieces were added above it, out of sight, so the sentinel never left the band either
  //    (the footer is kept out of anchoring now, collection.css).
  // A second observer, whose band runs from that band's foot all the way up the page, knows
  // whether the shopper has reached or passed the end of the grid. Whenever that holds and
  // the component is free (its classes change as each load ends), the next page is asked for.
  // The component's own checks still decide: a load under way, nothing more, the phones' cap.
  const sentinel = search.querySelector('[data-search-sentinel]');
  if (sentinel && search.dataset.infinite === 'true' && 'IntersectionObserver' in window) {
    let reachedEnd = false;
    let asking = false;
    const askForMore = () => {
      if (asking || !reachedEnd || typeof search.loadNextPage !== 'function') return;
      if (!search.hasMore || search.loadingMore || search.classList.contains('is-loading') || sentinel.hidden) return;
      asking = true;
      window.requestAnimationFrame(() => {
        asking = false;
        if (reachedEnd) search.loadNextPage();
      });
    };
    const band = mobileQuery.matches ? '100000px 0px 560px 0px' : '100000px 0px 1300px 0px';
    new IntersectionObserver((entries) => {
      reachedEnd = entries[entries.length - 1].isIntersecting;
      askForMore();
    }, { rootMargin: band }).observe(sentinel);
    new MutationObserver(askForMore).observe(search, { attributes: true, attributeFilter: ['class'] });
  }

  // ── 10. Phones: the end of the grid says where it stopped ──────────────────
  // On phones and tablets the component stops at a few hundred pieces (a long grid grows heavy
  // there) and says so above the grid, where nobody at the bottom looks. The end of the grid
  // says it here, with the count and a way on: "Show more" lets it load another 150, "Filter"
  // opens the filters.
  if (sentinel && results) {
    const end = document.createElement('div');
    end.className = 'plp-more';
    end.hidden = true;
    end.innerHTML = '<p class="plp-more__count" data-plp-more-count></p>'
      + '<div class="plp-more__actions">'
      + '<button type="button" class="button button--secondary plp-more__button" data-plp-more>Show More</button>'
      + '<button type="button" class="plp-more__filter" data-plp-more-filter>Filter</button>'
      + '</div>';
    sentinel.after(end);
    const endCount = end.querySelector('[data-plp-more-count]');
    const number = new Intl.NumberFormat('en-US');
    const shownCount = () => results.querySelectorAll('[data-product-card]').length;
    const paintEnd = () => {
      const shown = shownCount();
      const total = Number(search.totalFound) || 0;
      const capped = mobileQuery.matches && !search.hasMore && !search.loadingMore
        && total > shown && shown >= (Number(search.maxMobileRendered) || 350);
      if (!capped) {
        if (!end.hidden) end.hidden = true;
        return;
      }
      const text = `Showing ${number.format(shown)} of ${number.format(total)} pieces`;
      if (endCount.textContent !== text) endCount.textContent = text;
      if (end.hidden) end.hidden = false;
    };
    end.querySelector('[data-plp-more]').addEventListener('click', () => {
      search.maxMobileRendered = shownCount() + 150;
      search.hasMore = true;
      if (typeof search.renderStatus === 'function') search.renderStatus('');
      end.hidden = true;
      search.loadNextPage();
    });
    end.querySelector('[data-plp-more-filter]').addEventListener('click', () => {
      search.querySelector('[data-filter-toggle]')?.click();
    });
    new MutationObserver(paintEnd).observe(sentinel, { attributes: true, attributeFilter: ['hidden'] });
    new MutationObserver(paintEnd).observe(search, { attributes: true, attributeFilter: ['class'] });
  }

  syncSort();
  placeQuick();
  queueFit();
  shell.classList.add('plp-enhanced');
})();
